import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import CarList from "./components/CarList";
import Header from "./components/AppBar/Header";
import Login from "./components/Auth/Login";
import Signup from "./components/Auth/Signup";
import AdminDashboard from "./components/Admin/AdminDashboard";
import SuperAdminDashboard from "./components/SuperAdmin/SuperAdminDashboard";
import UserQuotation from "./components/Admin/UserQuotation";
import SuperAdminProfile from "./components/SuperAdmin/SuperAdminProfile";
import AllQuotations from "./components/Admin/AllQuotations";
import BackgroundSlider from "./components/BackgroundSlider";
import { Box, Button, Card, Typography, IconButton, useMediaQuery } from "@mui/material";
import HelpIcon from '@mui/icons-material/Help';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import GeeshImage from './Assets/Images/Geeshan.jpeg'
import BhathiyaImage from './Assets/Images/Bhathiya.jpeg'
import CloseIcon from '@mui/icons-material/Close';

// Use either local or production API URL
const APIURL = window.location.hostname === 'localhost' 
    ? "http://localhost:5400/auth" 
    : "https://crmb.apitrading.lk/auth";

function App() {
  const [userRole, setUserRole] = useState(localStorage.getItem("userRole"));
  const [userName, setUserName] = useState(localStorage.getItem("userName") || "");
  const [userId, setUserId] = useState(localStorage.getItem("userId") || "");
  const [isSessionValid, setIsSessionValid] = useState(
    localStorage.getItem("sessionExpiry") > Date.now()
  );
  const [showHelp, setShowHelp] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const helpCardRef = useRef(null);
  const isMobile = useMediaQuery('(max-width:600px)');

  // Help button styles
  const helpButtonStyles = {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    zIndex: 1000,
    animation: 'waveBorder 2s infinite',
    '@keyframes waveBorder': {
      '0%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.4)' },
      '70%': { boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)' },
      '100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' },
    },
    backgroundColor: '#1976d2',
    color: 'white',
    borderRadius: '25px',
    padding: '10px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '&:hover': {
      backgroundColor: '#1565c0',
    },
    transition: 'opacity 0.3s',
    opacity: isScrolling || isBottom || (isMobile && isBottom) ? 0 : 1,
  };

  // Help card styles
  const helpCardStyles = {
    position: 'fixed',
    bottom: '80px',
    right: '20px',
    zIndex: 1000,
    width: '300px',
    padding: '20px',
    borderRadius: '10px',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
    backgroundColor: 'white'
  };

  // ✅ Check session validity on initial load
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await fetch(`${APIURL}/session`, {
          method: "GET",
          credentials: "include",
        });

        const data = await response.json();
        if (data.success) {
          setUserRole(data.user.role);
          setUserName(data.user.name);
          setUserId(data.user.id);
          setIsSessionValid(true);

          const expiryTime = new Date(data.sessionExpiry).getTime();
          localStorage.setItem("sessionExpiry", expiryTime);
          localStorage.setItem("userRole", data.user.role);
          localStorage.setItem("userName", data.user.name);
          localStorage.setItem("userId", data.user.id);
        } else {
          localStorage.clear();
          setUserRole(null);
          setUserName("");
          setUserId("");
          setIsSessionValid(false);
        }
      } catch (error) {
        console.error("Session check failed:", error);
        localStorage.clear();
        setUserRole(null);
        setUserName("");
        setUserId("");
        setIsSessionValid(false);
      }
    };

    checkSession();
  }, []);

  // ✅ Redirect user to correct dashboard after login
  const getRedirectPath = () => {
    if (userRole === "superadmin") return "/carsSuperAdmin/createQuotation";
    if (userRole === "admin") return "/carsAdmin/createQuotation";
    return "/";
  };

  // Hide help card when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (helpCardRef.current && !helpCardRef.current.contains(event.target)) {
        setShowHelp(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [helpCardRef]);

  // Hide help button when scrolling
  useEffect(() => {
    let timeout;
    const handleScroll = () => {
      setIsScrolling(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setIsScrolling(false);
      }, 500);

      // Check if the user has scrolled to the bottom
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight) {
        setIsBottom(true);
      } else {
        setIsBottom(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timeout);
    };
  }, []);

  return (
    <Router>
      {isSessionValid ? (
        <Header
          onLogout={() => {
            localStorage.removeItem("userName");
            localStorage.removeItem("userId");
            localStorage.clear();
            setIsSessionValid(false);
            window.location.reload();
          }}
          userRole={userRole}
          userName={userName}
        />
      ) : (
        <BackgroundSlider />
      )}
      <div className="App">
        <Routes>
          <Route
            path="/"
            element={
              isSessionValid ? (
                <Navigate to={getRedirectPath()} />
              ) : (
                <Login
                  onLogin={(role, name, id) => {
                    setUserRole(role);
                    setUserName(name);
                    setUserId(id);
                    setIsSessionValid(true);
                    localStorage.setItem("userName", name);
                    localStorage.setItem("userId", id);
                  }}
                />
              )
            }
          />
          <Route path="/signup" element={<Signup />} />

          {/* ✅ SuperAdmin Routes */}
          <Route
            path="/carsSuperAdmin/createQuotation"
            element={
              isSessionValid && userRole === "superadmin" ? (
                <UserQuotation userRole={userRole}
                  userId={userId}
                  userName={userName} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/carsSuperAdmin/profile"
            element={
              isSessionValid && userRole === "superadmin" ? (
                <SuperAdminProfile />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/carsSuperAdmin/allquotations"
            element={
              isSessionValid && userRole === "superadmin" ? (
                <AllQuotations userRole={userRole} />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          {/* ✅ Admin Routes */}
          <Route
            path="/carsAdmin/createQuotation"
            element={
              isSessionValid && userRole === "admin" ? (
                <UserQuotation userRole={userRole}
                  userId={userId}
                  userName={userName} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/carsAdmin/allquotations"
            element={
              isSessionValid && userRole === "admin" ? (
                <AllQuotations userRole={userRole} />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          {/* ✅ Default User Routes */}
          <Route
            path="/cars"
            element={isSessionValid ? <CarList /> : <Navigate to="/" />}
          />
          <Route
            path="/createQuotation"
            element={isSessionValid ? <UserQuotation /> : <Navigate to="/" />}
          />

          {/* ✅ Catch-All Redirect */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>

        {/* Help Button and Card */}
        <IconButton 
          sx={{ 
            ...helpButtonStyles, 
            fontSize: { xs: '0.8rem', md: '1rem' }, // Smaller font size for mobile view
            padding: { xs: '6px', md: '8px' }, // Smaller padding for mobile view
            borderRadius: '50%', // Make the button circular
            width: { xs: '35px', md: '50px' }, // Set width for circular button
            height: { xs: '35px', md: '50px' } // Set height for circular button
          }} 
          onClick={() => {
            setShowHelp(!showHelp);
          }}
        >
          <HelpIcon sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }} /> {/* Smaller icon size for mobile view */}
        </IconButton>

        {showHelp && (
          <Card 
            ref={helpCardRef}
            sx={{ 
              ...helpCardStyles, 
              backgroundColor: 'rgba(0, 0, 0, 0.6)', 
              backdropFilter: 'blur(10px)',
              width: { xs: '150px', md: '200px' }, // Smaller width for mobile view
              padding: { xs: '16px', md: '20px' } // Smaller padding for mobile view
            }}
          >
            <IconButton
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                color: 'white',
                backgroundColor: 'rgb(255, 0, 0)',
                borderRadius: '50%',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'rotate(90deg) scale(1.1)',
                  backgroundColor: 'rgba(182, 0, 0, 0.9)',
                  boxShadow: '0 0 8px rgba(255, 0, 0, 0.5)'
                }
              }}
              onClick={() => setShowHelp(false)}
            >
              <CloseIcon />
            </IconButton>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                mb: 2, 
                paddingBottom: '8px', 
                textAlign: 'left', 
                fontWeight: 'bold', 
                color: 'white',
                fontSize: { xs: '1rem', md: '1.25rem' } // Smaller font size for mobile view
              }}
            >
              Get Help From Developer,
            </Typography>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: '8px', md: '12px' }, // Smaller gap for mobile view
                cursor: 'pointer',
                p: 1,
                mb: 1,
                mt: 1,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px'
                }
              }}
              onClick={() => {
                window.open('https://wa.me/94776048143', '_blank');
                setShowHelp(false);
              }}
            >
              <WhatsAppIcon sx={{ color: '#25D366', fontSize: { xs: '24px', md: '28px' } }} /> {/* Smaller icon size for mobile view */}
              <img 
                src={BhathiyaImage}
                alt="Bhathiya" 
                style={{ width: '25px', height: '25px', borderRadius: '50%', objectFit: 'cover' }} // Smaller image size for mobile view
              />
              <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, color: '#25D366' }}> {/* Smaller font size for mobile view */}
                Bhathiya
              </Typography>
            </Box>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: '8px', md: '12px' }, // Smaller gap for mobile view
                cursor: 'pointer',
                p: 1,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px'
                }
              }}
              onClick={() => {
                window.open('https://wa.me/94769117356', '_blank');
                setShowHelp(false);
              }}
            >
              <WhatsAppIcon sx={{ color: '#25D366', fontSize: { xs: '24px', md: '28px' } }} /> {/* Smaller icon size for mobile view */}
              <img 
                src={GeeshImage}
                alt="Geeshan" 
                style={{ width: '25px', height: '25px', borderRadius: '50%', objectFit: 'cover' }} // Smaller image size for mobile view
              />
              <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, color: '#25D366' }}> {/* Smaller font size for mobile view */}
                Geeshan
              </Typography>
            </Box>
          </Card>
        )}
       
      </div>
    </Router>
  );
}

export default App;
